import { AddIcon } from "@chakra-ui/icons";
import { Flex, Text, VStack, HStack, Button } from "@chakra-ui/react";
import {
  WalletImportMethod,
  getWalletName,
  PopulatedWallet,
} from "@syla/shared/types/models/WalletBase";
import { orderBy, some } from "lodash";
import React, { useMemo, useState, createContext } from "react";
import { Path } from "react-hook-form";
import { AiOutlineSync } from "react-icons/ai";
import { getDataSourceSortOptions } from "../../../../helper/dataSource/getDataSourceSortOptions";
import { useLocalStorage } from "../../../../hooks/localStorage/useLocalStorage";
import { useFuseSearch } from "../../../../hooks/useFuseSearch";
import { reSyncWallets } from "../../../../store/actions/reSyncWallets";
import { useGetWallets } from "../../../../store/actions/wallet";
import { useCurrentAccountStore } from "../../../../store/currentAccountStore";
import { useValueHoldings } from "../../../../store/useValueHoldings";
import { DataSourceSortOption } from "../../../../types/dataSource/dataSource";
import { PageHeadingContainer } from "../../../atoms/Containers";
import { PageHeading } from "../../../atoms/Headings";
import { SearchBar } from "../../../molecules/forms/SearchBar";
import {
  PageHeadingTag,
  PageHeadingTagContainer,
} from "../../../molecules/PageHeadingTag";
import { SingleSelector } from "../../../selector/SingleSelector";
import { getWalletHoldingsValue } from "./getWalletHoldingsValue";
import { RenderWallets } from "./RenderWallets";

interface MainDataSourceTableProps {
  addDataSourceButtonRef: any;
  openDrawer: () => void;
}

export type LoadingState = {
  isLoading: boolean;
  isStale: boolean;
};
export const DataSourceLoadingContext = createContext<LoadingState>({
  isLoading: false,
  isStale: false,
});

export const MainDataSourceTable = ({
  addDataSourceButtonRef,
  openDrawer,
}: MainDataSourceTableProps): JSX.Element => {
  const accountId = useCurrentAccountStore(({ accountId }) => accountId);

  // query for wallets data
  const {
    data: walletResponses,
    isLoading,
    isStale,
  } = useGetWallets(accountId);

  // value holdings
  const { data: holdingsValue } = useValueHoldings();

  // populate wallet holding market values
  for (const { _id: walletId, holdings } of walletResponses?.wallets ?? []) {
    for (const holding of holdings ?? []) {
      holding.marketValue = holdingsValue?.getWalletAssetValue({
        walletId,
        assetId: holding.asset._id,
      })?.marketValue;
    }
  }

  // Filter the wallets by search query
  const [walletsSearchQuery, setWalletsSearchQuery] = useState("");

  // Sort the user wallets by selection
  const [sortOption, setSortOption] = useLocalStorage<
    DataSourceSortOption | undefined
  >("dataSourcesSortBy", undefined);

  // filter the user wallets by search query
  const searchFilteredWallets: PopulatedWallet[] = useFuseSearch(
    walletResponses?.wallets || [],
    ["dataSource.name", "dataSource.type", "dataSource.code", "customName"],
    walletsSearchQuery,
    {
      threshold: 0.4,
    },
  );

  // wallets memo, returns search filtered wallets if no sort options selected
  const wallets = useMemo(() => {
    if (!sortOption) return searchFilteredWallets;

    const sortKeys: (Path<PopulatedWallet> | ((w: PopulatedWallet) => any))[] =
      [];
    const sortOrder: (boolean | "desc" | "asc")[] = [];

    if (sortOption === DataSourceSortOption.NameAscending) {
      sortKeys.push((w) =>
        getWalletName({ wallet: w, dataSource: w.dataSource }),
      );
      sortOrder.push("asc");
    } else if (sortOption === DataSourceSortOption.NameDescending) {
      sortKeys.push((w) =>
        getWalletName({ wallet: w, dataSource: w.dataSource }),
      );
      sortOrder.push("desc");
    } else if (sortOption === DataSourceSortOption.MostTransactions) {
      sortKeys.push("transactionCount");
      sortOrder.push("desc");
    } else if (sortOption == DataSourceSortOption.HighestValue) {
      sortKeys.push((walletWithHoldings) => {
        const value = getWalletHoldingsValue({
          holdingsValue,
          walletWithHoldings,
        })?.toNumber(); // use toNumber to enable regular sort

        return value ?? Number.NEGATIVE_INFINITY; // null values should be treated as less than all other values
      });
      sortOrder.push("desc");
    } else {
      // default is date added
      sortKeys.push("createdAt");
      sortOrder.push("asc");
    }

    return orderBy(searchFilteredWallets, sortKeys, sortOrder);
  }, [holdingsValue, searchFilteredWallets, sortOption]);

  // boolean if a an api imported wallet is in the wallets array
  const apiImportedWalletExists = some(
    wallets,
    (wallet) => wallet.importMethod == WalletImportMethod.API,
  );

  // re-sync all wallets handler
  const reSyncAllWalletsHandler = async () => {
    // verify there is an api imported wallet in the wallets array
    if (!apiImportedWalletExists) return;

    // filtered array of only api imported wallets
    const apiWallets = wallets.filter(
      (wallet) => wallet.importMethod == WalletImportMethod.API,
    );
    await reSyncWallets(accountId, apiWallets);
  };

  return (
    <>
      <PageHeadingContainer>
        <PageHeadingTagContainer>
          <PageHeading>Data Sources</PageHeading>
          {walletResponses != null && (
            <PageHeadingTag>
              {walletResponses.wallets.length.toLocaleString()}
            </PageHeadingTag>
          )}
        </PageHeadingTagContainer>

        <Flex direction={{ base: "column", xs: "row" }} gap={4}>
          {apiImportedWalletExists && (
            <Button
              backgroundColor="white"
              variant="outline"
              colorScheme="gray"
              leftIcon={<AiOutlineSync />}
              onClick={reSyncAllWalletsHandler}
            >
              Sync All
            </Button>
          )}
          <Button
            ref={addDataSourceButtonRef}
            onClick={openDrawer}
            variant="solid"
            leftIcon={<AddIcon />}
            colorScheme="primary"
          >
            Add Data Source
          </Button>
        </Flex>
      </PageHeadingContainer>

      <VStack direction="column" w="full" spacing={4}>
        <VStack alignItems="start" w="full" spacing={2}>
          <Text>Your exchanges, wallets, blockchains and other platforms.</Text>

          <HStack w="full" spacing={4}>
            <SearchBar
              value={walletsSearchQuery}
              placeholder="Search your data sources"
              onChange={(e) => setWalletsSearchQuery(e.target.value)}
              autoFocus
              backgroundColor="white"
            />
            <SingleSelector
              enableDeselect
              placeholder="Sort By"
              options={getDataSourceSortOptions()}
              value={sortOption}
              onChange={setSortOption}
              placement="bottom-end"
              buttonProps={{
                flex: "0 0 auto",
                backgroundColor: "white",
              }}
            />
          </HStack>
        </VStack>
        {/* ----------------------- data source table row -----------------------  */}
        <DataSourceLoadingContext.Provider
          value={{
            isLoading,
            isStale,
          }}
        >
          <RenderWallets walletResponses={wallets} />
        </DataSourceLoadingContext.Provider>
      </VStack>
    </>
  );
};
